import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

const IDLE_TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2 hours
const CHECK_INTERVAL_MS = 60 * 1000; // Check every minute

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private lastActivity = Date.now();
  private timeoutCheckInterval: any = null;
  private isInitialized = false;

  constructor(
    private authService: AuthService,
    private router: Router,
    private toast: ToastService,
    private zone: NgZone
  ) {}

  init(): void {
    if (this.isInitialized) return;
    this.isInitialized = true;

    // Track user activity
    const updateActivity = () => {
      this.lastActivity = Date.now();
    };

    // Use passive event listeners for better performance
    window.addEventListener('mousemove', updateActivity, { passive: true });
    window.addEventListener('keydown', updateActivity, { passive: true });
    window.addEventListener('click', updateActivity, { passive: true });
    window.addEventListener('scroll', updateActivity, { passive: true });
    window.addEventListener('touchstart', updateActivity, { passive: true });

    // Check for timeout periodically
    this.zone.runOutsideAngular(() => {
      this.timeoutCheckInterval = setInterval(() => {
        this.checkIdleTimeout();
      }, CHECK_INTERVAL_MS);
    });
  }

  private checkIdleTimeout(): void {
    const now = Date.now();
    const idleTime = now - this.lastActivity;

    // Only logout if user is authenticated
    const token = this.authService.getToken();
    if (!token) return;

    if (idleTime >= IDLE_TIMEOUT_MS) {
      this.zone.run(() => {
        this.expireSession();
      });
    }
  }

  private expireSession(): void {
    // Clear the interval
    if (this.timeoutCheckInterval) {
      clearInterval(this.timeoutCheckInterval);
      this.timeoutCheckInterval = null;
    }

    // Store a flag for showing message on login page
    sessionStorage.setItem('session_expired', 'true');

    // Logout and redirect
    this.authService.logout();
    
    // Show toast after short delay (after redirect completes)
    setTimeout(() => {
      this.toast.warning('Session expired due to inactivity. Please log in again.', 6000);
    }, 500);
  }

  resetActivity(): void {
    this.lastActivity = Date.now();
  }

  destroy(): void {
    if (this.timeoutCheckInterval) {
      clearInterval(this.timeoutCheckInterval);
      this.timeoutCheckInterval = null;
    }
    this.isInitialized = false;
  }
}
